import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../components/ui/card";
import { 
  Target,
  Eye,
  Heart,
  Users,
  Shield,
  Globe,
  Handshake
} from "lucide-react";
import abdulKarim from "../assets/team/abdul-karim.jpg";

const About = () => {
  const { t } = useTranslation();

  const values = [
    { icon: Shield, title: 'Trust', desc: 'Building secure trade relationships through verified suppliers and trade assurance' },
    { icon: Globe, title: 'Connectivity', desc: 'Bridging the Gulf and East Africa through seamless digital commerce' },
    { icon: Handshake, title: 'Partnership', desc: 'Supporting businesses of all sizes to thrive in international trade' },
  ];

  const team = [
    { name: 'Abdul Karim Nsimbe', role: 'CEO & Founder', bio: 'Cross-border trade & tech founder', image: abdulKarim },
    { name: 'Grace Wanjiku', role: 'Head of Operations, East Africa', bio: 'Expert in East African markets and supply chain' },
    { name: 'Omar Hassan', role: 'Head of Supplier Relations', bio: 'Building partnerships across the GCC region' },
    { name: 'Sarah Kimani', role: 'Head of Customer Success', bio: 'Ensuring buyer satisfaction and trade success' },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="about-page">
      {/* Hero */}
      <div 
        className="relative py-24 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1739298061766-e2751d92e9db?w=1920&q=80')`
        }}
      >
        <div className="absolute inset-0 bg-[#0D4F8B]/80" />
        <div className="container-main relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('about.title')}
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto text-lg">
            Connecting Gulf excellence with African opportunity
          </p>
        </div>
      </div>

      <div className="container-main py-16">
        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <Card className="card-base">
            <CardContent className="p-8">
                <div className="w-14 h-14 bg-[#0D4F8B]/10 rounded-full flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-[#0D4F8B]" aria-hidden="true" />
                </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('about.mission.title')}
              </h2>
              <p className="text-slate-600 leading-relaxed">
                {t('about.mission.desc')}
              </p>
            </CardContent>
          </Card>

          <Card className="card-base">
            <CardContent className="p-8">
                <div className="w-14 h-14 bg-[#10B981]/10 rounded-full flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-[#10B981]" aria-hidden="true" />
                </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('about.vision.title')}
              </h2>
              <p className="text-slate-600 leading-relaxed">
                {t('about.vision.desc')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Values */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('about.values.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="card-base hover-lift">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#0D4F8B]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-8 h-8 text-[#0D4F8B]" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600">{value.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Team */}
        <div>
          <div className="text-center mb-12">
            <span className="overline text-[#0D4F8B] mb-3 block">{t('about.team.overline')}</span>
            <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('about.team.title')}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((member, index) => (
              <Card key={index} className="card-base hover-lift">
                <CardContent className="p-6 text-center">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-20 h-20 rounded-full object-cover mx-auto mb-4"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-gradient-to-br from-[#0D4F8B] to-[#10B981] rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-2xl">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </div>
                  )}
                  <h3 className="font-semibold text-slate-900 mb-1">{member.name}</h3>
                  <p className="text-sm text-[#0D4F8B] font-medium mb-2">{member.role}</p>
                  <p className="text-sm text-slate-500">{member.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
